import { Fragment, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import {
  deleteCampaignByIdData,
  fetchCampaignsData,
} from "../../stores/thunks/campaignsThunk.tsx";
import campaignEmpty from "/images/empty_states/campaign-empty.svg";
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import {
  CampaignItemDataType,
  CreatorBriefItemDataType,
  PlatformItemDataType,
  TimeFrameDataType,
} from "../../stores/types/campaignsTypes.tsx";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";
import { Link } from "react-router";
import { InfoCampaignCards } from "./components/infoCampaignCards/InfoCampaignCards.tsx";
import { getPlatformIcon } from "../../helpers/functions/getPlatformIcon.tsx";
import { useDebounce } from "../../helpers/hooks/useDebounce.tsx";
import { fullFormatNumbers } from "../../helpers/functions/formatNumber.ts";
import {
  RevActionsHeader,
  RevButton,
  RevCard,
  RevDialog,
  RevEmptyPage,
  RevInput,
  RevLink,
  RevSpinner,
  RevTable,
  RevTooltip,
  RevActionButton,
} from "@revmatics/catalyst";
import { useFlags } from "launchdarkly-react-client-sdk";

const columnHelper = createColumnHelper<CampaignItemDataType>();

export const ManageCampaignsPage = () => {
  const dispatch = useAppDispatch();
  const campaigns = useAppSelector((state) => state.campaigns.campaigns);
  const isLoading = useAppSelector((state) => state.campaigns.isLoading);
  const isLoadingDelete = useAppSelector(
    (state) => state.campaigns.isLoadingDelete,
  );
  const {
    createCampaign: ffCreateCampaign,
    deleteCampaign: ffDeleteCampaign,
    detailsCampaign: ffDetailsCampaign,
    isTutorialCampaigns: ffTutorialCampaigns,
  } = useFlags();
  const [searchValue, setSearchValue] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    useState<CampaignItemDataType | null>(null);
  const debouncedSearch = useDebounce(searchValue, 500);

  useEffect(() => {
    if (!campaigns) {
      dispatch(fetchCampaignsData(""));
    }
  }, [dispatch, campaigns]);

  const displayCampaigns = useMemo(() => {
    if (campaigns && campaigns.length > 0) {
      return campaigns.filter((item) =>
        item.name.toLowerCase().includes(debouncedSearch.toLowerCase()),
      );
    } else {
      return null;
    }
  }, [debouncedSearch, campaigns]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<CampaignItemDataType, any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseColumns: ColumnDef<CampaignItemDataType, any>[] = [
      columnHelper.accessor("name", {
        id: "campaignName",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Name
          </span>
        ),
        cell: (info) => {
          const originalData = info.row.original;
          return (
            <div className="flex flex-col gap-0.5">
              {ffDetailsCampaign ? (
                <RevLink
                  pathname=""
                  search={`campaignId=${originalData.id}`}
                  text={originalData.name}
                  className="w-fit"
                />
              ) : (
                <span>{originalData.name}</span>
              )}
              <span className="typography-body-xs text-monochrome-500">
                ID:{originalData.id}
              </span>
            </div>
          );
        },
        enableSorting: true,
        size: 300,
      }),
      columnHelper.accessor("total_budget", {
        id: "totalBudget",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Total Budget
          </span>
        ),
        cell: (info) => {
          const totalBudget = info.getValue() as number;
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              ${fullFormatNumbers({ value: totalBudget })}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor((row) => row.platforms, {
        id: "platforms",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Platforms
          </span>
        ),
        cell: (info) => {
          const platforms = info.getValue() as PlatformItemDataType[];
          const originalData = info.row.original;
          if (!platforms || platforms.length === 0) return "";
          return (
            <div className="flex items-center gap-1">
              {platforms.slice(0, 3).map((platform: PlatformItemDataType) => {
                const icon = getPlatformIcon(platform.name.toLowerCase());
                return icon ? (
                  <FontAwesomeIcon
                    icon={icon}
                    className="text-monochrome-500 text-md"
                    key={`personas-list-platform-${originalData.id}-${platform.id}`}
                  />
                ) : (
                  <span
                    className="text-xs text-monochrome-500"
                    key={`personas-list-platform-${originalData.id}-${platform.id}`}
                  >
                    {platform.name}
                  </span>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
        size: 180,
      }),
      columnHelper.accessor("creator_briefs", {
        id: "creatorBriefs",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Creator Count
          </span>
        ),
        cell: (info) => {
          const creatorBriefs = info.getValue() as CreatorBriefItemDataType[];
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {creatorBriefs.length}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      columnHelper.accessor("time_frame", {
        id: "timeFrame",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-sm text-monochrome-900">
            Time Frame
          </span>
        ),
        cell: (info) => {
          const timeFrame = info.getValue() as TimeFrameDataType;
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {`${formatDateToMonthDayYear(timeFrame.start)} - ${formatDateToMonthDayYear(timeFrame.end)}`}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
    ];
    const actionColumn = columnHelper.display({
      id: "actions",
      header: () => <span></span>,
      cell: (info) => {
        const originalData = info.row.original;
        return (
          <div className="flex items-center gap-1 space-x-1 justify-center">
            <Menu>
              {({ open }) => {
                return (
                  <div className="relative">
                    {/* Conditional Render:
                We swap 'position' to 'left' as requested.
                The Tooltip only mounts when 'open' is false.
            */}
                    {!open ? (
                      <RevTooltip
                        content="More Actions"
                        position="left"
                        className="w-[90px]"
                      >
                        <MenuButton
                          data-testid="more-actions"
                          as={RevActionButton}
                        />
                      </RevTooltip>
                    ) : (
                      <MenuButton as={RevActionButton} />
                    )}

                    <MenuItems
                      anchor={{ to: "bottom end", gap: 4 }}
                      transition
                      className="origin-top transition duration-200 outline-0 ease-out shadow-elevation-2 rounded-lg bg-white py-2 z-10 flex flex-col w-[200px]"
                    >
                      {ffDetailsCampaign && (
                        <MenuItem as={Fragment}>
                          <Link
                            to={`?campaignId=${originalData.id}`}
                            className="w-full typography-body-sm text-start text-monochrome-900 py-2 px-3 disabled:text-monochrome-400 hover:bg-monochrome-50"
                          >
                            View / Edit Campaign
                          </Link>
                        </MenuItem>
                      )}
                      {ffDeleteCampaign && (
                        <MenuItem as={Fragment}>
                          <button
                            onClick={() => setIsDeleteDialogOpen(originalData)}
                            aria-label="delete campaign"
                            className="w-full typography-body-sm text-start text-red-500 py-2 px-3 disabled:text-monochrome-400 hover:bg-monochrome-50"
                          >
                            Delete Campaign
                          </button>
                        </MenuItem>
                      )}
                    </MenuItems>
                  </div>
                );
              }}
            </Menu>
          </div>
        );
      },
      size: 48,
    });

    return [
      ...baseColumns,
      ...(ffDeleteCampaign || ffDetailsCampaign ? [actionColumn] : []),
    ];
  }, [ffDeleteCampaign, ffDetailsCampaign]);

  if (isLoading && !campaigns) {
    return (
      <div className="h-full p-4 sm:p-6 lg:p-8 text-center">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if (!isLoading && campaigns && campaigns.length === 0) {
    return (
      <RevEmptyPage
        image={campaignEmpty}
        title="No Campaigns"
        text={ffCreateCampaign ? "Let's create your first campaign now!" : ""}
        to={ffCreateCampaign ? "" : "/personas"}
        search={ffCreateCampaign ? "?campaignId=create" : ""}
        textBtn={ffCreateCampaign ? "Create Campaign" : "Back"}
        data-testid="empty-campaigns-page"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="manage-campaigns-page">
      <RevActionsHeader
        title="Manage Campaigns"
        headerClassName="!mb-0"
        videoBtn={ffTutorialCampaigns}
      >
        {ffCreateCampaign && (
          <RevLink
            text="Create Campaign"
            displayAs="button"
            buttonSize="medium"
            pathname=""
            search="?campaignId=create"
            className="w-fit"
          />
        )}
      </RevActionsHeader>
      <InfoCampaignCards />
      <RevCard padding="p-0">
        <div className="p-4 flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="w-full md:w-[313px]">
            <RevInput
              name="search"
              type="text"
              label=""
              size="md"
              placeholder="Search"
              enableClearButton
              onClear={() => setSearchValue("")}
              iconLeft={faMagnifyingGlass}
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue}
              className="w-fit"
            />
          </div>
        </div>
        <RevTable<CampaignItemDataType>
          data={displayCampaigns || []}
          columns={columns}
          initialPageSize={5}
          enableGlobalFilter={false}
          enablePagination={true}
        />
      </RevCard>
      <RevDialog
        isOpen={!!isDeleteDialogOpen}
        setIsOpen={() => setIsDeleteDialogOpen(null)}
        title="Delete Campaign?"
        btnList={[
          <RevButton
            key="cancel-campaign-delete"
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsDeleteDialogOpen(null)}
          />,
          <RevButton
            key="delete-campaign-delete"
            text="Yes, delete"
            variant="primary"
            buttonColor="error"
            handleClick={() => {
              if (isDeleteDialogOpen) {
                dispatch(
                  deleteCampaignByIdData({
                    id: isDeleteDialogOpen.id.toString(),
                    name: isDeleteDialogOpen.name,
                    callback: () => setIsDeleteDialogOpen(null),
                  }),
                );
              }
            }}
            isLoading={isLoadingDelete}
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900">
          Are you sure you want to delete the campaign "
          {isDeleteDialogOpen?.name}
          "? This action can't be undone.
        </p>
      </RevDialog>
    </div>
  );
};
